import { useState, useEffect } from 'react';
import {
    APISubmitAnswer, APIGetTestDetail, APIMarkQuestion,
    APIDeleteQuestion, APIUpdateQuestion, APIFlagQuestion,
} from '../../api';

/**
 * Вся механика сессии теста: загрузка, ответы, навигация, язык, пометки, XP.
 *
 * Вёрстки здесь нет — её дают скины. Состояние живёт выше вида, поэтому смена
 * скина посреди теста не роняет прогресс: подменяется только оболочка.
 *
 * Код перенесён из TestSessionPage.jsx дословно, без правок поведения.
 * Имена обработчиков намеренно оставлены прежними — так тело рендера не
 * пришлось трогать вообще.
 */
export function useTestSession({ testId, token, navigate, isMobile }) {
    const [test, setTest] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState([]);
    const [textAnswer, setTextAnswer] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [marking, setMarking] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showResult, setShowResult] = useState(false);
    const [submissionResult, setSubmissionResult] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    
    const [instantAnswerMode, setInstantAnswerMode] = useState(false);
    const [instantAnswerResult, setInstantAnswerResult] = useState(null);

    // Flagged questions (stored in localStorage)
    const [flaggedQuestions, setFlaggedQuestions] = useState(() => {
        const saved = localStorage.getItem('flaggedQuestions');
        return saved ? JSON.parse(saved) : [];
    });

    // Состояние для модального окна результатов
    const [resultsModalOpen, setResultsModalOpen] = useState(false);

    // Состояние для модального окна редактирования вопроса (админ)
    const [editModalOpen, setEditModalOpen] = useState(false);

    // Состояние для админского флага "требует проверки"
    const [reviewFlagOpen, setReviewFlagOpen] = useState(false);
    const [reviewNote, setReviewNote] = useState('');
    const [reviewFlagging, setReviewFlagging] = useState(false);

    // Состояние для модального окна жалобы
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [xpGained, setXpGained] = useState(null);
    const [levelUp, setLevelUp] = useState(false);
    const [panelHidden, setPanelHidden] = useState(false);

    // Language toggle (EN/RU)
    const [lang, setLang] = useState(() => {
        try { return localStorage.getItem('testLang') || 'en'; }
        catch { return 'en'; }
    });
    const toggleLang = () => {
        const next = lang === 'en' ? 'ru' : 'en';
        setLang(next);
        try { localStorage.setItem('testLang', next); } catch {}
    };
    // Helper: pick RU or EN text
    const t = (en, ru) => lang === 'ru' && ru ? ru : en;

    // Tour banner: show if user is mid-tour (detected via localStorage).
    const [tourBannerVisible] = useState(() => {
        try {
            return localStorage.getItem('apex_tour_pending') === '1'
                && localStorage.getItem('apex_tour_done') !== '1';
        } catch { return false; }
    });

    const returnToPlan = () => {
        navigate('/plans');
    };

    useEffect(() => {
        setLoading(true);
        fetchTestData();
    }, [testId]);

    // Pre-populate answered state for the current question whenever test loads
    // or the current index changes. Without this, the first question of a returning
    // user appears unanswered even if user_answer exists in fetched data.
    useEffect(() => {
        if (!test?.test_questions) return;
        const targetQuestion = test.test_questions[currentQuestionIndex];
        if (!targetQuestion) return;
        if (targetQuestion.is_answered && !targetQuestion.is_skipped && targetQuestion.user_answer) {
            const previousAnswers = targetQuestion.user_answer.selected_answers?.map(a => a.id) || [];
            setSelectedAnswers(previousAnswers);
            setShowResult(true);
            setSubmissionResult({
                is_correct: targetQuestion.user_answer.is_correct,
                explanation: targetQuestion.question?.explanation,
                explanation_ru: targetQuestion.question?.explanation_ru
            });
        }
    }, [test, currentQuestionIndex]);

    // Навигация стрелками клавиатуры
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Игнорируем если фокус в текстовом поле
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            if (!test?.test_questions) return;

            if (e.key === 'ArrowLeft' && currentQuestionIndex > 0) {
                handleQuestionNavigation(currentQuestionIndex - 1);
            } else if (e.key === 'ArrowRight' && currentQuestionIndex < test.test_questions.length - 1) {
                handleQuestionNavigation(currentQuestionIndex + 1);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentQuestionIndex, test?.test_questions?.length]);

    const fetchTestData = async () => {
        try {
            // setLoading(true);
            const response = await APIGetTestDetail(token, testId);
            if (response.success) {
                setTest(response.test);
            }
        } catch (error) {
            console.error('Error fetching test:', error);
        } finally {
            setLoading(false);
        }
    };

    const currentQuestion = test?.test_questions?.[currentQuestionIndex];
    const question = currentQuestion?.question;

    // Проверяем, отвечен ли уже вопрос
    const userAnswer = currentQuestion?.user_answer;

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const handleInstantAnswerCheck = async (answerId) => {
        if (!currentQuestion || !instantAnswerMode) return;
        
        const question = currentQuestion.question;
        
        if (question.type !== 'single') {
            // Режим мгновенного ответа работает только для одиночного выбора
            return;
        }
        
        // Ключ до ответа сервер больше не отдаёт (аудит 06.09.2026), поэтому
        // сначала отправляем ответ, а вердикт и разбор берём из ответа сервера.
        try {
            const response = await APISubmitAnswer(token, {
                test_question_id: currentQuestion.id,
                selected_answer_ids: [answerId],
                text_answer: '',
                is_skipped: false
            });
            const correct = response?.correct_answers?.[0];
            setInstantAnswerResult({
                isCorrect: !!response?.is_correct,
                selectedAnswerId: answerId,
                correctAnswerId: correct?.id,
                explanation: response?.explanation,
                explanation_ru: response?.explanation_ru,
                correctAnswerDescription: undefined
            });

            // Обновляем данные теста и ждём
            await fetchTestData();
        } catch (error) {
            console.error('Error submitting instant answer:', error);
        }
    };

    const handleAnswerSelect = (answerId) => {
        if (question?.type === 'single') {
            setSelectedAnswers([answerId]);
        } else if (question?.type === 'multiple') {
            setSelectedAnswers(prev => 
                prev.includes(answerId) 
                    ? prev.filter(id => id !== answerId)
                    : [...prev, answerId]
            );
        }
    };
    
    // Функция для маркировки вопроса
    const handleMarkQuestion = async () => {
        if (!currentQuestion) return;
        
        setMarking(true);
        try {
            const response = await APIMarkQuestion(token, {
                test_question_id: currentQuestion.id,
                is_marked: !currentQuestion.is_marked
            });
            
            if (response.success) {
                // Обновляем данные теста
                fetchTestData();
            }
        } catch (error) {
            console.error('Error marking question:', error);
        } finally {
            setMarking(false);
        }
    };

    // Функция для удаления вопроса из базы данных
    const handleDeleteQuestion = async () => {
        if (!currentQuestion || !question) return;

        const confirmDelete = window.confirm(
            `Удалить вопрос "${question.text.substring(0, 50)}..." из базы данных? Это действие нельзя отменить.`
        );

        if (!confirmDelete) return;

        setDeleting(true);
        try {
            const response = await APIDeleteQuestion(token, question.id);

            if (response.success) {
                // Переходим к следующему вопросу или предыдущему
                if (currentQuestionIndex < test.test_questions.length - 1) {
                    setCurrentQuestionIndex(prev => prev);
                } else if (currentQuestionIndex > 0) {
                    setCurrentQuestionIndex(prev => prev - 1);
                }
                // Обновляем данные теста
                fetchTestData();
            }
        } catch (error) {
            console.error('Error deleting question:', error);
            alert('Ошибка при удалении вопроса');
        } finally {
            setDeleting(false);
        }
    };

    // Флажок "плохой вопрос" - сохраняем в localStorage
    const handleFlagQuestion = () => {
        if (!question) return;

        const questionId = question.id;
        let newFlagged;

        if (flaggedQuestions.includes(questionId)) {
            newFlagged = flaggedQuestions.filter(id => id !== questionId);
        } else {
            newFlagged = [...flaggedQuestions, questionId];
        }

        setFlaggedQuestions(newFlagged);
        localStorage.setItem('flaggedQuestions', JSON.stringify(newFlagged));
    };

    // Админский флаг "требует проверки" — сохранить с заметкой
    const handleReviewFlagSave = async () => {
        if (!question) return;
        setReviewFlagging(true);
        try {
            const response = await APIFlagQuestion(token, question.id, reviewNote, true);
            if (response.success) {
                // Обновим локально
                if (question) {
                    question.needs_review = true;
                    question.review_note = reviewNote;
                }
                setReviewFlagOpen(false);
                setReviewNote('');
            }
        } catch (e) {
            alert('Ошибка при пометке вопроса');
        } finally {
            setReviewFlagging(false);
        }
    };

    // Снять админский флаг "требует проверки"
    const handleReviewFlagRemove = async () => {
        if (!question) return;
        setReviewFlagging(true);
        try {
            const response = await APIFlagQuestion(token, question.id, '', false);
            if (response.success) {
                if (question) {
                    question.needs_review = false;
                    question.review_note = '';
                }
                setReviewFlagOpen(false);
                setReviewNote('');
            }
        } catch (e) {
            alert('Ошибка при снятии флага');
        } finally {
            setReviewFlagging(false);
        }
    };

    // Открыть модалку флажка (с существующей заметкой если есть)
    const openReviewFlag = () => {
        setReviewNote(question?.review_note || '');
        setReviewFlagOpen(true);
    };

    // Сохранение отредактированного вопроса (админ)
    const handleSaveQuestion = async (formData) => {
        if (!question) return;

        try {
            const response = await APIUpdateQuestion(token, question.id, formData);
            if (response.success !== false) {
                // Обновляем данные теста
                await fetchTestData();
                setEditModalOpen(false);
            } else {
                alert('Ошибка при сохранении: ' + (response.error || 'Неизвестная ошибка'));
            }
        } catch (error) {
            console.error('Error saving question:', error);
            alert('Ошибка при сохранении вопроса');
        }
    };

    const isQuestionFlagged = question && flaggedQuestions.includes(question.id);

    const handleSubmitAnswer = async (isSkipped = false) => {
        if (!currentQuestion) return;
        // Prevent double submission
        if (submitting) return;

        setSubmitting(true);
        try {
            const response = await APISubmitAnswer(token, {
                test_question_id: currentQuestion.id,
                selected_answer_ids: isSkipped ? [] : selectedAnswers,
                text_answer: isSkipped ? '' : textAnswer,
                is_skipped: isSkipped
            });

            if (response.success) {
                setSubmissionResult(response);

                // XP toast
                if (response.xp_earned) {
                    setXpGained(response.xp_earned);
                    setLevelUp(response.level_up || false);
                    setTimeout(() => setXpGained(null), 2000);
                }

                // Обновляем данные теста и ждём завершения
                await fetchTestData();

                // В учебном режиме показываем результат
                if (test.mode === 'study' && !isSkipped) {
                    setShowResult(true);
                } else {
                    // В тестовом режиме или при пропуске сразу переходим дальше
                    moveToNextQuestion(response);
                }
            } else {
                console.error('Submit answer failed:', response.error);
            }
        } catch (error) {
            console.error('Error submitting answer:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const moveToNextQuestion = (response) => {
        // Сбрасываем состояние ответов
        setSelectedAnswers([]);
        setTextAnswer('');
        setShowResult(false);
        setSubmissionResult(null);
        setInstantAnswerResult(null);

        // Переходим к следующему вопросу или показываем результаты
        if (response?.test_completed || isLastQuestion) {
            setResultsModalOpen(true);
        } else if (currentQuestionIndex < test.test_questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handleContinue = async () => {
        // Сбрасываем состояние ответов
        setSelectedAnswers([]);
        setTextAnswer('');
        setShowResult(false);
        setSubmissionResult(null);
        setInstantAnswerResult(null);

        // Проверяем, последний ли вопрос
        if (isLastQuestion) {
            // Показываем модальное окно с результатами
            setResultsModalOpen(true);
        } else {
            // Переходим к следующему вопросу
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handleQuestionNavigation = (index) => {
        const targetQuestion = test?.test_questions?.[index];

        setCurrentQuestionIndex(index);
        setInstantAnswerResult(null);

        // Если вопрос был отвечен (не пропущен) — восстанавливаем состояние
        if (targetQuestion?.is_answered && !targetQuestion?.is_skipped && targetQuestion?.user_answer) {
            const previousAnswers = targetQuestion.user_answer.selected_answers?.map(a => a.id) || [];
            setSelectedAnswers(previousAnswers);
            setShowResult(true);
            setSubmissionResult({
                is_correct: targetQuestion.user_answer.is_correct,
                explanation: targetQuestion.question?.explanation,
                explanation_ru: targetQuestion.question?.explanation_ru
            });
        } else {
            // Пропущенные или неотвеченные — чистое состояние
            setSelectedAnswers([]);
            setTextAnswer('');
            setShowResult(false);
            setSubmissionResult(null);
        }

        if (isMobile) {
            setSidebarOpen(false);
        }
    };

    const handleResetAnswer = () => {
        setSelectedAnswers([]);
    };
    // В оригинале объявлялась ниже по файлу, но moveToNextQuestion и
    // handleContinue обращаются к ней — в хуке нужна явно.
    const isLastQuestion = currentQuestionIndex === test?.test_questions?.length - 1;

    return {
        test, setTest, currentQuestionIndex, setCurrentQuestionIndex,
        selectedAnswers, setSelectedAnswers, textAnswer, setTextAnswer,
        loading, setLoading, submitting, setSubmitting,
        marking, setMarking, deleting, setDeleting,
        showResult, setShowResult, submissionResult, setSubmissionResult,
        sidebarOpen, setSidebarOpen, instantAnswerMode, setInstantAnswerMode,
        instantAnswerResult, setInstantAnswerResult, flaggedQuestions, setFlaggedQuestions,
        resultsModalOpen, setResultsModalOpen, editModalOpen, setEditModalOpen,
        reviewFlagOpen, setReviewFlagOpen, reviewNote, setReviewNote,
        reviewFlagging, setReviewFlagging, reportModalOpen, setReportModalOpen,
        xpGained, setXpGained, levelUp, setLevelUp,
        panelHidden, setPanelHidden, lang, setLang,
        toggleLang, t, tourBannerVisible, returnToPlan,
        fetchTestData, currentQuestion, question, userAnswer,
        toggleSidebar, handleInstantAnswerCheck, handleAnswerSelect, handleMarkQuestion,
        handleDeleteQuestion, handleFlagQuestion, handleReviewFlagSave, handleReviewFlagRemove,
        openReviewFlag, handleSaveQuestion, isQuestionFlagged, handleSubmitAnswer,
        moveToNextQuestion, handleContinue, handleQuestionNavigation, handleResetAnswer,
        isLastQuestion,
    };
}
